import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STATUS_TONE = {
  awaiting_review: "bg-amber-100 text-amber-700", needs_revision: "bg-orange-100 text-orange-700",
  approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700", superseded: "bg-slate-100 text-slate-400",
};
const PRIO_TONE = { high: "bg-rose-100 text-rose-700", medium: "bg-blue-100 text-blue-700", low: "bg-slate-100 text-slate-500" };
const fmtDT = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Widget({ icon, label, value, tone }) {
  const Ic = Icons[icon];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Ic className="h-5 w-5" /></div>
      <div><div className="text-2xl font-bold text-slate-900 leading-none">{value}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
    </div>
  );
}

export default function OperationsQueue({ queueKey }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: "days_waiting", dir: "desc" });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [views, setViews] = useState([]);
  const [dialog, setDialog] = useState(null); // {item, action}
  const [categories, setCategories] = useState([]);
  const [detail, setDetail] = useState(null);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ sort: sort.key, dir: sort.dir, page, page_size: 25 });
    if (q) params.set("q", q);
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const { data } = await api.get(`/ops/queues/${queueKey}/items?${params}`);
    setData(data); setSelected(new Set());
  }, [queueKey, q, filters, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/ops/reviewers").then((r) => setReviewers(r.data.reviewers)).catch(() => {});
    api.get(`/ops/queues/${queueKey}/views`).then((r) => setViews(r.data.views)).catch(() => {});
    api.get("/documents-intel/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, [queueKey]);

  // load rich detail (version history, notes, relations) when preview opens
  useEffect(() => {
    if (!preview?.document_id) { setDetail(null); return; }
    setDetail(null);
    api.get(`/documents-intel/${preview.document_id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [preview]);

  const def = data?.definition;
  const items = data?.items || [];
  const w = data?.widgets || {};

  const doAction = async (item, action, payload = {}) => {
    try {
      await api.post(`/ops/queues/${queueKey}/items/${item.id}/action`, { action, ...payload });
      toast.success(`${action.replace(/_/g, " ")} done`);
      setDialog(null); setPreview(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulk = async (action, payload = {}) => {
    try {
      const { data } = await api.post(`/ops/queues/${queueKey}/bulk`, { action, ids: [...selected], ...payload });
      toast.success(data.message); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveView = async () => {
    const name = window.prompt("Save current filters as view:");
    if (!name) return;
    const { data } = await api.post("/ops/views", { name, filters: { q, ...filters }, queue_key: queueKey });
    setViews((v) => [...v, data]); toast.success("View saved");
  };
  const applyView = (v) => { setQ(v.filters.q || ""); const f = { ...v.filters }; delete f.q; setFilters(f); setPage(1); };

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));
  const allSel = items.length > 0 && items.every((i) => selected.has(i.id));
  const previewUrl = preview ? `${API}/files/${preview.document_id}/download?auth=${getToken()}` : null;

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-5" data-testid="ops-queue">
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">Operations</p>
        <h1 className="text-2xl font-bold text-slate-900">{def?.title || "Queue"}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{def?.description}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Widget icon="FileSearch" label="Awaiting Review" value={w.awaiting_review ?? "—"} tone="bg-amber-100 text-amber-700" />
        <Widget icon="CheckCircle2" label="Approved Today" value={w.approved_today ?? "—"} tone="bg-emerald-100 text-emerald-700" />
        <Widget icon="RotateCcw" label="Requiring Client Action" value={w.requiring_client_action ?? "—"} tone="bg-orange-100 text-orange-700" />
        <Widget icon="Timer" label="Avg Review Time (hrs)" value={w.avg_review_hours ?? "—"} tone="bg-blue-100 text-blue-700" />
      </div>

      {/* toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input defaultValue={q} onChange={(e) => { clearTimeout(searchTimer.current); const v = e.target.value; searchTimer.current = setTimeout(() => { setQ(v); setPage(1); }, 350); }}
            data-testid="ops-search" placeholder="Search client, engagement, document…" className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        {def?.filters?.map((f) => f.type === "select" ? (
          <select key={f.key} value={filters[f.key] || ""} onChange={(e) => { setFilters((p) => ({ ...p, [f.key]: e.target.value })); setPage(1); }}
            data-testid={`ops-filter-${f.key}`} className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">{f.label}: All</option>
            {f.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select>
        ) : (
          <input key={f.key} value={filters[f.key] || ""} onChange={(e) => { setFilters((p) => ({ ...p, [f.key]: e.target.value })); setPage(1); }}
            placeholder={f.label} data-testid={`ops-filter-${f.key}`} className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm w-36" />
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="ops-views-btn"><Icons.Bookmark className="h-4 w-4" />Views</button></DropdownMenuTrigger>
          <DropdownMenuContent>
            {views.length === 0 && <DropdownMenuItem disabled>No saved views</DropdownMenuItem>}
            {views.map((v) => <DropdownMenuItem key={v.id} onClick={() => applyView(v)}>{v.name}</DropdownMenuItem>)}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={saveView} data-testid="ops-save-view"><Icons.Plus className="h-4 w-4 mr-2" />Save current view</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* bulk bar */}
      {selected.size > 0 && (
        <div className="bg-blue-600 text-white rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="ops-bulk-bar">
          <span className="font-medium">{selected.size} selected</span>
          <button onClick={() => bulk("approve")} data-testid="ops-bulk-approve" className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5"><Icons.CheckCheck className="h-4 w-4" />Approve</button>
          <select onChange={(e) => e.target.value && bulk("assign_reviewer", { reviewer_id: e.target.value })} data-testid="ops-bulk-assign" className="bg-white/15 rounded-lg px-2 py-1.5 text-white"><option value="">Assign reviewer…</option>{reviewers.map((r) => <option className="text-slate-800" key={r.id} value={r.id}>{r.name}</option>)}</select>
          <button onClick={() => { const c = window.prompt("New category:"); c && bulk("change_category", { category: c }); }} className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5"><Icons.Tag className="h-4 w-4" />Category</button>
          <button onClick={() => { if (window.confirm(`Archive ${selected.size} document(s)?`)) bulk("archive"); }} data-testid="ops-bulk-archive" className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5"><Icons.Archive className="h-4 w-4" />Archive</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-white/80 hover:text-white">Clear</button>
        </div>
      )}

      {/* table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="ops-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2.5"><input type="checkbox" checked={allSel} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} data-testid="ops-select-all" /></th>
            {def?.columns?.map((c) => <th key={c.key} onClick={() => toggleSort(c.key)} className="px-3 py-2.5 font-medium cursor-pointer hover:text-slate-600">{c.label}{sort.key === c.key ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}</th>)}
            <th className="px-3 py-2.5"></th>
          </tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={20} className="px-5 py-12 text-center text-slate-400">🎉 Nothing in the queue. You're all caught up.</td></tr> :
            items.map((i) => (
              <tr key={i.id} data-testid={`ops-row-${i.id}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-3 py-2.5"><input type="checkbox" checked={selected.has(i.id)} onChange={(e) => setSelected((s) => { const n = new Set(s); e.target.checked ? n.add(i.id) : n.delete(i.id); return n; })} data-testid={`ops-select-${i.id}`} /></td>
                {def.columns.map((c) => (
                  <td key={c.key} className="px-3 py-2.5 text-slate-700">
                    {c.type === "badge" && c.key === "review_status" ? <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[i[c.key]] || "bg-slate-100"}`}>{(i[c.key] || "").replace(/_/g, " ")}</span> :
                     c.type === "badge" && c.key === "priority" ? <span className={`text-[11px] px-2 py-0.5 rounded-full ${PRIO_TONE[i[c.key]] || "bg-slate-100"}`}>{i[c.key]}</span> :
                     c.type === "datetime" ? fmtDT(i[c.key]) :
                     c.key === "document_name" ? <button onClick={() => setPreview(i)} className="text-blue-600 hover:underline max-w-[180px] truncate inline-block align-bottom">{i[c.key]}</button> :
                     <span className={c.key === "client_name" ? "font-medium text-slate-800" : ""}>{i[c.key] ?? "—"}</span>}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button data-testid={`ops-row-menu-${i.id}`} className="p-1.5 rounded hover:bg-slate-100"><Icons.MoreHorizontal className="h-4 w-4 text-slate-500" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setPreview(i)}><Icons.Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => doAction(i, "approve")} data-testid={`ops-approve-${i.id}`}><Icons.Check className="h-4 w-4 mr-2" />Approve</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "request_changes" })}><Icons.RotateCcw className="h-4 w-4 mr-2" />Request Changes</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "reject" })}><Icons.X className="h-4 w-4 mr-2" />Reject</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "reassign" })}><Icons.UserCog className="h-4 w-4 mr-2" />Reassign Reviewer</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "add_note" })}><Icons.StickyNote className="h-4 w-4 mr-2" />Add Note</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "rename" })} data-testid={`ops-rename-${i.id}`}><Icons.Pencil className="h-4 w-4 mr-2" />Rename</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "change_category" })}><Icons.Tag className="h-4 w-4 mr-2" />Change Category</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "link_request" })} data-testid={`ops-link-${i.id}`}><Icons.Link className="h-4 w-4 mr-2" />Link to Request</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { if (window.confirm("Archive this document?")) doAction(i, "archive"); }} data-testid={`ops-archive-${i.id}`}><Icons.Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate(`/crm/${i.client_id}`)}><Icons.Briefcase className="h-4 w-4 mr-2" />Open Client Workspace</DropdownMenuItem>
                      {i.engagement_id && <DropdownMenuItem onClick={() => navigate(`/client-success`)}><Icons.FileSignature className="h-4 w-4 mr-2" />Open Engagement</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      {data && data.total > data.page_size && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>{(page - 1) * data.page_size + 1}–{Math.min(page * data.page_size, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Prev</button>
            <button disabled={page * data.page_size >= data.total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {/* preview drawer */}
      <Sheet open={!!preview} onOpenChange={() => setPreview(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="ops-preview">
          {preview && (
            <>
              <SheetHeader><SheetTitle className="truncate">{preview.document_name}</SheetTitle><SheetDescription className="sr-only">Document preview and review actions</SheetDescription></SheetHeader>
              <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>{preview.client_name} · {preview.client_number}</span><span>{preview.engagement_number}</span>
                <span>Uploaded {fmtDT(preview.uploaded_at)} by {preview.uploaded_by}</span><span>{preview.days_waiting}d waiting</span>
              </div>
              <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden h-[45vh] bg-slate-50">
                <iframe title="doc" src={previewUrl} className="w-full h-full" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-2"><Icons.Download className="h-4 w-4" />Download</a>
                <button onClick={() => doAction(preview, "approve")} data-testid="ops-preview-approve" className="inline-flex items-center gap-1.5 text-sm bg-emerald-600 text-white rounded-lg px-3 py-2"><Icons.Check className="h-4 w-4" />Approve</button>
                <button onClick={() => setDialog({ item: preview, action: "request_changes" })} className="inline-flex items-center gap-1.5 text-sm bg-orange-500 text-white rounded-lg px-3 py-2"><Icons.RotateCcw className="h-4 w-4" />Request Changes</button>
                <button onClick={() => setDialog({ item: preview, action: "reject" })} className="inline-flex items-center gap-1.5 text-sm bg-rose-600 text-white rounded-lg px-3 py-2"><Icons.X className="h-4 w-4" />Reject</button>
                <button onClick={() => setDialog({ item: preview, action: "rename" })} data-testid="ops-preview-rename" className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-2"><Icons.Pencil className="h-4 w-4" />Rename</button>
              </div>

              {/* metadata */}
              <div className="mt-5 grid grid-cols-2 gap-y-2 gap-x-4 text-xs" data-testid="ops-preview-meta">
                <Meta label="Category" value={detail?.document?.category} />
                <Meta label="Tax Year" value={detail?.document?.tax_year} />
                <Meta label="Source" value={(detail?.document?.upload_source || preview.upload_source || "").replace(/_/g, " ")} />
                <Meta label="Version" value={`v${detail?.document?.version ?? 1}`} />
                <Meta label="Linked Request" value={detail?.linked_request?.title} />
                <Meta label="Engagement" value={detail?.engagement?.engagement_number} />
              </div>

              {/* AI placeholders */}
              <div className="mt-4 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3" data-testid="ops-preview-ai">
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5 flex items-center gap-1"><Icons.Sparkles className="h-3.5 w-3.5" />Document Intelligence (coming soon)</div>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-400">Classification: Pending</span>
                  <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-400">Extraction: Future Feature</span>
                  <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-400">Quality: Not Yet Available</span>
                </div>
              </div>

              {/* version history */}
              {detail?.version_history?.length > 1 && (
                <div className="mt-4" data-testid="ops-preview-versions">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Version History</div>
                  <div className="space-y-1">
                    {detail.version_history.map((v) => (
                      <div key={v.id} className="flex items-center justify-between text-xs border border-slate-100 rounded px-2.5 py-1.5">
                        <span className="text-slate-700">v{v.version} · {(v.status || "").replace(/_/g, " ")} {v.is_current && <span className="text-emerald-600">(current)</span>}</span>
                        <span className="text-slate-400">{v.uploaded_by_name} · {fmtDT(v.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* internal notes */}
              {detail?.review_notes?.length > 0 && (
                <div className="mt-4" data-testid="ops-preview-notes">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Internal Notes (staff only)</div>
                  <div className="space-y-1.5">
                    {detail.review_notes.map((n, idx) => <div key={idx} className="text-xs text-slate-600"><span className="font-medium">{n.by}</span>: {n.note}</div>)}
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {dialog && <ActionDialog dialog={dialog} reviewers={reviewers} categories={categories} onClose={() => setDialog(null)} onConfirm={(payload) => doAction(dialog.item, dialog.action, payload)} />}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-slate-400">{label}</div>
      <div className="text-slate-700 font-medium capitalize">{value || "—"}</div>
    </div>
  );
}

function ActionDialog({ dialog, reviewers, categories, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [name, setName] = useState(dialog.item?.document_name || "");
  const [category, setCategory] = useState(dialog.item?.category || "");
  const [requestId, setRequestId] = useState("");
  const [openReqs, setOpenReqs] = useState([]);
  const { action, item } = dialog;
  const titles = { request_changes: "Request Changes", reject: "Reject Document", add_note: "Add Internal Note",
    reassign: "Reassign Reviewer", rename: "Rename Document", change_category: "Change Category", link_request: "Link to Document Request" };
  useEffect(() => {
    if (action === "link_request" && item?.client_id)
      api.get(`/documents-intel/requests/open/${item.client_id}`).then((r) => setOpenReqs(r.data.requests)).catch(() => {});
  }, [action, item]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ops-action-dialog">
        <DialogHeader><DialogTitle>{titles[action]}</DialogTitle><DialogDescription className="sr-only">Provide the required details and confirm.</DialogDescription></DialogHeader>
        {action === "reassign" ? (
          <select value={reviewer} onChange={(e) => setReviewer(e.target.value)} data-testid="ops-reassign-select" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
            <option value="">Select reviewer…</option>{reviewers.map((r) => <option key={r.id} value={r.id}>{r.name} {r.role_label ? `· ${r.role_label}` : ""}</option>)}
          </select>
        ) : action === "rename" ? (
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="ops-rename-input" placeholder="New display name…" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
        ) : action === "change_category" ? (
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ops-category-select" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
            <option value="">Select category…</option>{categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        ) : action === "link_request" ? (
          <select value={requestId} onChange={(e) => setRequestId(e.target.value)} data-testid="ops-linkreq-select" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
            <option value="">Select open request…</option>{openReqs.map((r) => <option key={r.id} value={r.id}>{r.title} ({(r.request_status || "").replace(/_/g, " ")})</option>)}
            {openReqs.length === 0 && <option disabled>No open requests for this client</option>}
          </select>
        ) : (
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="ops-action-note"
            placeholder={action === "request_changes" ? "What does the client need to fix?" : "Note…"} className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />
        )}
        <DialogFooter>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button data-testid="ops-action-confirm" onClick={() => {
            if (action === "reassign") return onConfirm({ reviewer_id: reviewer });
            if (action === "rename") { if (!name.trim()) return toast.error("A name is required."); return onConfirm({ display_filename: name.trim() }); }
            if (action === "change_category") { if (!category) return toast.error("Select a category."); return onConfirm({ category }); }
            if (action === "link_request") { if (!requestId) return toast.error("Select a request."); return onConfirm({ request_id: requestId }); }
            if (action === "request_changes" && !note.trim()) return toast.error("A note is required.");
            onConfirm({ note });
          }} className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium">Confirm</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
